import React, { useEffect } from 'react';
import { Button, Card, Typography, Space, Tag } from 'antd';
import { AudioOutlined, StopOutlined, CheckCircleOutlined, SoundOutlined, RobotOutlined, FileTextOutlined, UserOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Title, Paragraph } = Typography;

interface ConsentRecordingProps {
    candidateInfo: { firstName?: string; lastName?: string } | null;
    isRecording: boolean;
    isLoading: boolean;
    isSubmitting: boolean;
    recordingTime: number;
    consentBlob: Blob | null;
    transcription?: string;
    isTranscribing?: boolean;
    videoRef: React.RefObject<HTMLVideoElement>;
    streamRef: React.RefObject<MediaStream | null>;
    onStartRecording: () => void;
    onStopRecording: () => void;
    onStopAndSubmit?: () => void;
    onSaveRecording: (blob: Blob, transcript?: string) => void;
    onReRecord: () => void;
    consentVerificationFailed?: boolean;
}

const ConsentRecording: React.FC<ConsentRecordingProps> = ({
    candidateInfo,
    isRecording,
    isLoading,
    isSubmitting,
    recordingTime,
    consentBlob,
    transcription = '',
    isTranscribing = false,
    videoRef,
    streamRef,
    onStartRecording,
    onStopRecording,
    onStopAndSubmit,
    onSaveRecording,
    onReRecord,
    consentVerificationFailed = false
}) => {
    // const candidateName = candidateInfo?.firstName
    //     ? `${candidateInfo.firstName}${candidateInfo.lastName ? ' ' + candidateInfo.lastName : ''}`
    //     : 'Your Name';
    const consentStatement = `By proceeding with this interview, I voluntarily provide my explicit consent to have my voice recorded during this AI-powered interview session. I understand that my audio responses will be recorded, processed, and analyzed by an automated AI system for the purpose of interview evaluation, scoring, and recruitment assessment.`;

    // Debug: Log when consentBlob changes
    useEffect(() => {
        console.log('ConsentRecording - consentBlob changed:', consentBlob ? `Blob size: ${consentBlob.size}, type: ${consentBlob.type}` : 'null');
    }, [consentBlob]);

    return (
        <div className="bg-[#F3F7F7] flex h-screen flex-col overflow-hidden p-4">
            {/* Header */}
            <div className="flex justify-between items-center border-b border-gray-200 p-4 bg-white rounded-t-lg">
                <img src={require('./../../../assets/logo/logo.png')} alt="logo" className="w-40" />
                <div className="w-40"></div>
            </div>

            <div className="flex-1 flex gap-4 p-4">
                {/* First Column - AI Avatar */}
                <div className="w-1/3 flex flex-col gap-4">
                    {/* AI Avatar Area - Sufficient height */}
                    <div className="relative bg-black rounded-lg overflow-hidden flex items-center justify-center" style={{ minHeight: '350px', height: 'fit-content', padding: '40px' }}>
                        <div className="flex flex-col items-center justify-center">
                            <div className="w-32 h-32 rounded-full bg-gray-800 flex items-center justify-center mb-4">
                                <RobotOutlined style={{ fontSize: '64px', color: '#fff' }} />
                            </div>
                            <div className="text-white text-center">
                                {isRecording ? (
                                    <div className="flex flex-col items-center gap-2">
                                        <span className="text-lg font-semibold">Listening...</span>
                                        <div className="flex items-center gap-1">
                                            {[...Array(5)].map((_, i) => (
                                                <div
                                                    key={i}
                                                    className="w-1 bg-green-500 rounded-full"
                                                    style={{
                                                        animation: `listen 1s ease-in-out infinite`,
                                                        animationDelay: `${i * 0.1}s`,
                                                        height: '20px'
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <span className="text-lg font-semibold">AI Interviewer</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Second Column - Consent Content */}
                <div className="w-2/3 flex flex-col min-w-0 overflow-hidden">
                    <Card
                        className="flex-1 flex flex-col shadow-lg overflow-auto"
                        style={{ borderRadius: '12px' }}
                        bodyStyle={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '24px', overflow: 'auto' }}
                    >
                        <div className="flex-1 flex flex-col justify-center min-h-0">
                            {/* Header Section - Enhanced */}
                            <div className="mb-6 flex-shrink-0">
                                <div className="flex items-start gap-2 mb-2">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg flex-shrink-0">
                                        <FileTextOutlined style={{ fontSize: '18px', color: '#fff' }} />
                                    </div>
                                    <div className="flex-1">
                                        <Title
                                            level={2}
                                            className="mb-0"
                                            style={{
                                                fontSize: '32px',
                                                fontWeight: 700,
                                                background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 50%, #8b5cf6 100%)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                backgroundClip: 'text',
                                                letterSpacing: '-0.5px',
                                                lineHeight: '1'
                                            }}
                                        >
                                            Consent Recording
                                        </Title>
                                        <Paragraph
                                            className="text-sm text-gray-600 mb-0"
                                            style={{
                                                fontSize: '14px',
                                                lineHeight: '1.2',
                                                color: '#4b5563',
                                                marginTop: '0'
                                            }}
                                        >
                                            Before we begin the interview, please read and record the following consent statement clearly.
                                        </Paragraph>
                                    </div>
                                </div>
                                <div className="w-20 h-0.5 bg-gradient-to-r from-blue-400 via-indigo-400 to-blue-400 rounded-full"></div>
                            </div>

                            {/* Consent Statement Box */}
                            <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-8 shadow-md hover:shadow-lg transition-shadow">
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
                                            <CheckCircleOutlined style={{ fontSize: '18px', color: '#fff' }} />
                                        </div>
                                    </div>
                                    <Paragraph
                                        className="text-lg font-semibold text-gray-800 mb-0 leading-relaxed"
                                        style={{ lineHeight: '1.6' }}
                                    >
                                        {consentStatement}
                                    </Paragraph>
                                </div>
                            </div>

                            {/* Verification Failed Message */}
                            {consentVerificationFailed && !isRecording && (
                                <div className="w-full mb-6 mx-auto">
                                    <div className="bg-red-50 border-2 border-red-300 rounded-xl p-5 shadow-md">
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 mt-1">
                                                <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                                                    <span className="text-white font-bold text-lg">!</span>
                                                </div>
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-red-800 mb-2" style={{ fontSize: '16px' }}>
                                                    Consent Statement Not Matched
                                                </h4>
                                                <p className="text-red-700 mb-0" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                                                    The consent statement you read does not match the required text. Please read the statement again clearly and completely. Make sure to read all the words as shown above.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Transcription Display */}
                            {(isRecording || transcription) && (
                                <div className="w-full mb-6 mx-auto relative">
                                    <div
                                        className={`bg-white rounded-xl border-2 transition-all ${isRecording
                                            ? 'border-red-300 bg-red-50'
                                            : consentVerificationFailed
                                                ? 'border-red-300 bg-red-50'
                                                : transcription
                                                    ? 'border-green-300 bg-green-50'
                                                    : 'border-gray-200 bg-gray-50'
                                            }`}
                                        style={{
                                            boxShadow: isRecording ? '0 0 20px rgba(239, 68, 68, 0.1)' : 'none'
                                        }}
                                    >
                                        {/* Header with Your Answer, Status Tags, and Timer */}
                                        <div className="flex items-center justify-between p-4 border-b border-gray-200 gap-3">
                                            {/* Left: Your Answer */}
                                            <div className="flex items-center gap-2">
                                                <UserOutlined style={{ fontSize: '18px', color: '#10b981' }} />
                                                <span className="font-semibold text-gray-800" style={{ fontSize: '16px' }}>
                                                    Your Answer
                                                </span>
                                            </div>

                                            {/* Middle: Status Tags */}
                                            <div className="flex items-center gap-2 flex-1 justify-center">
                                                {isRecording && (
                                                    <Tag
                                                        color="red"
                                                        style={{
                                                            border: '1px solid #ef4444',
                                                            background: '#fee2e2',
                                                            color: '#dc2626',
                                                            fontSize: '13px',
                                                            padding: '4px 12px',
                                                            borderRadius: '4px'
                                                        }}
                                                        icon={<AudioOutlined />}
                                                    >
                                                        Recording...
                                                    </Tag>
                                                )}
                                                {isTranscribing && (
                                                    <Tag
                                                        color="orange"
                                                        style={{
                                                            border: '1px solid #f97316',
                                                            background: '#fff7ed',
                                                            color: '#ea580c',
                                                            fontSize: '13px',
                                                            padding: '4px 12px',
                                                            borderRadius: '4px'
                                                        }}
                                                        icon={<SoundOutlined />}
                                                    >
                                                        Transcribing...
                                                    </Tag>
                                                )}
                                            </div>

                                            {/* Right: Timer */}
                                            {isRecording && (
                                                <div className="flex items-center gap-2">
                                                    <ClockCircleOutlined style={{ fontSize: '16px', color: '#ef4444' }} />
                                                    <span
                                                        className="text-base font-semibold"
                                                        style={{
                                                            color: '#ef4444'
                                                        }}
                                                    >
                                                        {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Transcript Content */}
                                        <div
                                            className="p-5"
                                            style={{
                                                fontSize: '16px',
                                                lineHeight: '1.8',
                                                color: transcription ? '#1f2937' : '#9ca3af',
                                                fontStyle: transcription ? 'normal' : 'italic',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                minHeight: '150px',
                                                maxHeight: '300px',
                                                overflowY: 'auto'
                                            }}
                                        >
                                            {transcription || (
                                                <div className="flex flex-col items-center justify-center h-full text-center" style={{ minHeight: '150px' }}>
                                                    <div className="mb-3">
                                                        <AudioOutlined style={{ fontSize: '48px', color: '#ef4444' }} />
                                                    </div>
                                                    <p className="text-red-600 font-medium" style={{ fontSize: '16px' }}>Listening... Speak now</p>
                                                    <p className="text-sm text-gray-500 mt-1">Your speech will appear here</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Recording Controls */}
                            <div className="flex flex-col items-end gap-4">
                                {consentBlob && !consentVerificationFailed ? (
                                    // Recording successful - show nothing (will auto-advance)
                                    <></>
                                ) : (
                                    <>
                                        {!isRecording && (
                                            <Button
                                                type="primary"
                                                size="large"
                                                icon={<AudioOutlined />}
                                                onClick={onStartRecording}
                                                loading={isLoading}
                                                style={{
                                                    height: '56px',
                                                    fontSize: '18px',
                                                    paddingLeft: '40px',
                                                    paddingRight: '40px',
                                                    borderRadius: '12px',
                                                    background: consentVerificationFailed
                                                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                                        : 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                                                    border: 'none',
                                                    boxShadow: consentVerificationFailed
                                                        ? '0 4px 16px rgba(245, 158, 11, 0.4)'
                                                        : '0 4px 16px rgba(59, 130, 246, 0.4)',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {consentVerificationFailed ? 'Re-record Consent' : 'Start Recording'}
                                            </Button>
                                        )}
                                        {isRecording && (
                                            <div className="flex items-end gap-4">
                                                <Button
                                                    type="primary"
                                                    size="large"
                                                    icon={<StopOutlined />}
                                                    onClick={onStopAndSubmit || onStopRecording}
                                                    loading={isSubmitting}
                                                    disabled={recordingTime < 5}
                                                    style={{
                                                        height: '56px',
                                                        fontSize: '18px',
                                                        paddingLeft: '40px',
                                                        paddingRight: '40px',
                                                        borderRadius: '12px',
                                                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                                        border: 'none',
                                                        boxShadow: '0 4px 16px rgba(239, 68, 68, 0.4)',
                                                        fontWeight: 600,
                                                        opacity: recordingTime < 5 ? 0.5 : 1
                                                    }}
                                                >
                                                    {isSubmitting ? 'Submitting...' : recordingTime < 5 ? `Record at least 5s (${recordingTime}s)` : 'Stop & Submit'}
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ConsentRecording;
